import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  IconButton,
  CardActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import ImageBox from "../../components/Image/ImageBox";
import { ItemListQuery, Option } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { useTranslation } from "react-i18next";
import ItemListActions from "../../components/Item/ItemListActions";
import LoadingSection from "../../components/LoadingSection";
import StockInformationDialog from "../../components/Item/StockInformationDialog";
import {
  SupplierAutoComplete,
  ItemAutoComplete,
  ItemCategoryAutoComplete,
  ItemGroupAutoComplete,
  ProgramAutoComplete,
  CollectionAutoComplete,
  GenderAutoComplete,
  ProductGroupAutoWithoutItemCategoryComplete
} from "../../components/AutoComplete/AutoCompletes";
import AsyncAutoComplete from "../../components/AsyncAutoComplete";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ResponsivePagination from "../../components/ResponsivePagination";
import BlockIcon from "@mui/icons-material/Block";
import { ItemListViewModel } from "../../Types/ExportTypes";
import ScannerDialog from "../../components/Scanner/ScannerDialog";


const ItemList: React.FC = () => {
  const [items, setItems] = useState<ItemListViewModel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [query, setQuery] = useCachedListQuery({
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
  });
  const [filter, setFilter] = useState<string>("");
  const [filterProperty, setFilterProperty] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { authService } = useAuth();
  const { navigate } = useNavigation();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [stockItemNo, setStockItemNo] = useState<string | null>(null)
  const isStockInformationOpen = stockItemNo !== null && stockItemNo.trim().length != 0;
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const isBooleanProperty = filterProperty === "ext_Blocked" || filterProperty === "ext_SalesBlocked";

  useEffect(() => {
    if (isBooleanProperty) setFilter("true");
  }, [isBooleanProperty]);


  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await authService.post<{ items: ItemListViewModel[]; count: number }>(
        "/itemApi/GetItemList",
        query
      );
      setItems(response.items);
      setTotalItems(response.count);
    } catch (error : any) {
      console.error("Failed to fetch items:", error);
      showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [query]);



  useEffect(() => {
    if (loading) showLoading();
    else hideLoading();
  }, [loading, showLoading, hideLoading]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    var localFilterProperty = filterProperty;
    //if (!filter) return;
    if (!localFilterProperty?.trim() && filter?.trim()) {
      localFilterProperty = "Default";
    }

    if (localFilterProperty?.trim() && filter?.trim()) {
      const newFilter = buildQueryFilter(localFilterProperty, filter);
      const isDuplicate = query.filters?.some(f =>
        f.property === newFilter.property && f.value === newFilter.value
      );
      if (isDuplicate) return;

      const newFilters = [...(query.filters ?? []), newFilter];
      setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
    } else {
      // No filterProperty: just update the query with the raw filter string
      setQuery({ ...query, page: 0, filter });
    }

    setFilter("");
    setFilterProperty("");
  };

  const handleSelectItem = (itemNo: string) => {
    navigate({ path: "/purchase/item/edit", params: { itemNo } });
  };

  const handlePageChange = (newPage: number) => {
    setQuery({ ...query, page: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setQuery({ ...query, pageSize: newPageSize, page: 0 });
  };

  const handleCreateItem = () => {
    navigate({ path: "/purchase/item/edit", params: { itemNo: "" } });
  };
  const handleNavigateItemImport = () => {
    // navigate({ path: "/purchase/item/import/list"});
    //navigate to create directly instead
    navigate({ path: "/purchase/item/import/edit", params: {} });
  };


  const scanBarcode = (barcode: string) => {
    const newFilter = buildQueryFilter("ext_Barcode", barcode);
    const newFilters = [...(query.filters ?? []), newFilter];
    setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
    return true;
  };


  const handleBarcodeScan = useCallback((code: string) => scanBarcode(code), [scanBarcode]);

  const handleExport = async () => {
    if (!query.filter && (!query.filters || query.filters.length == 0)) {
      showSnackbar(t("dialog.missingFilters"), "warning");
    }

    try {
      const response = await authService.post<Blob>(
        "/itemApi/GetItemListCsv",
        query,
        "blob"
      );

      const csvBlob = response instanceof Blob
        ? response
        : new Blob([response as any], { type: "text/csv;charset=utf-8" });

      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ItemList.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error:any) {
      console.error("Failed to download Excel", error);
      showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
    }
  };

  const handleScannerOpen = useCallback(() => {
    setIsScannerOpen(true);
  }, []);

  const handleScannerClose = useCallback(() => {
    setIsScannerOpen(false);
  }, []);
  return (
    <CenteredListCard title={t("common.items")}>
      <StockInformationDialog
        open={isStockInformationOpen}
        onClose={() => setStockItemNo(null)}
        itemNo={stockItemNo || undefined}
      />

      <ScannerDialog
        open={isScannerOpen}
        onClose={handleScannerClose}
        onScan={handleBarcodeScan}
      />
      {/* Search Field */}
      <Box sx={{ marginBottom: 2 }}>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleCreateItem}
        >
          {t("common.create")}
        </Button>
        <ItemListActions
          onImport={() => handleNavigateItemImport()}
          onExport={() => handleExport()}

          customActions={[
                {
              label: t("common.scanBarcode"),
                   onClick: () => handleScannerOpen(),
                },
          ]}
        />
      </Box>
      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{
          display: "flex",
          gap: 2,
          marginBottom: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={5} lg={1}>
            <TextField
              select
              label={t("common.field")}
              value={filterProperty}
              onChange={(e) => setFilterProperty(e.target.value)}
              SelectProps={{ native: true }}
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <option value="">{t("common.default")}</option>
              <option value="ext_No">{t("common.itemNo")}</option>
              <option value="ext_SupplierNo">{t("common.supplier")}</option>
              <option value="ext_ItemCategoryCode">{t("common.itemCategory")}</option>
              <option value="ext_ProductGroupCode">{t("common.productGroup")}</option>
              <option value="ext_ItemGroupCode">{t("common.itemGroup")}</option>
              <option value="ext_ProgramCode">{t("common.program")}</option>
              <option value="ext_Collection">{t("common.collection")}</option>
              <option value="ext_Gender">{t("common.gender")}</option>
              <option value="ext_Blocked">{t("common.blocked")}</option>
              <option value="ext_SalesBlocked">{t("common.salesBlocked")}</option>
            </TextField>
          </Grid>
          <Grid item xs={7} lg={8}>
            {(() => {
              switch (filterProperty) {
                case "ext_SupplierNo":
                  return (
                    <SupplierAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_No":
                  return (
                    <ItemAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_ItemCategoryCode":
                  return (
                    <ItemCategoryAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_ProductGroupCode":
                  return (
                    <ProductGroupAutoWithoutItemCategoryComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_ItemGroupCode":
                  return (
                    <ItemGroupAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_ProgramCode":
                  return (
                    <ProgramAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_Collection":
                  return (
                    <CollectionAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_Gender":
                  return (
                    <GenderAutoComplete
                      onChange={(o) => setFilter(o?.value || "")}
                    />
                  );
                case "ext_Blocked":
                  return (
                    <TextField
                      select
                      label={t("common.value")}
                      variant="outlined"
                      fullWidth
                      onChange={(e) => setFilter(e.target.value)}
                      value={filter}
                      SelectProps={{ native: true }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="true">{t("common.yes")}</option>
                      <option value="false">{t("common.no")}</option>
                    </TextField>
                  );
                case "ext_SalesBlocked":
                  return (
                    <TextField
                      select
                      label={t("common.value")}
                      variant="outlined"
                      fullWidth
                      onChange={(e) => setFilter(e.target.value)}
                      value={filter}
                      SelectProps={{ native: true }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="true">{t("common.yes")}</option>
                      <option value="false">{t("common.no")}</option>
                    </TextField>
                  );

                default:
                  return (
                    <TextField
                      label={t("common.searchItems")}
                      variant="outlined"
                      fullWidth
                      onChange={(event) => setFilter(event.target.value)}
                      value={filter}
                      disabled={loading}
                    />
                  );
              }
            })()}
          </Grid>
          <Grid item xs={12} lg={3}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {t("common.search")}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {query.filters?.map((f, index) => (
          <Chip
            key={index}
            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
            onDelete={() => {
              const newFilters = query.filters?.filter((_, i) => i !== index);
              setQuery({ ...query, filters: newFilters, page: 0 });
            }}
            deleteIcon={<DeleteIcon />}
          />
        ))}
      </Box>

      {/* Card Layout for Items */}
      <LoadingSection loading={loading}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {items.map((item) => (
            <Grid item
              xs={12}
              sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
              md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}

              key={item.no}>
              <Card
                onClick={() => handleSelectItem(item.no)}
                sx={{
                  cursor: "pointer",
                  transition: "box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: 4,
                    backgroundColor: "secondary.main",
                  },
                }}
              >
                <ImageBox
                  imageUrl={item.img_Url}
                  altText={item.description}
                  fallbackText="No Image"
                />
                <CardContent>
                  <ListCardHeader
                    icon={<Inventory2Icon fontSize="small" color="action" />}
                    title={item.no}
                    status={(item.salesBlocked || item.blocked)
                      ? {
                        label: item.blocked ? t("common.blocked") : t("common.salesBlocked"),
                        color: "warning",
                        icon: <BlockIcon fontSize="small" />,
                      }
                      : undefined}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {item.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("common.barcode")}: {item.barcode}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("common.created")}: {item.created ? new Date(item.created).toLocaleString() : ""}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("common.updated")}: {item.updated ? new Date(item.updated).toLocaleString() : ""}
                  </Typography>
                </CardContent>

                <CardActions sx={{}}>


                  <Button
                    component="a"
                    href="#"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStockItemNo(item.no); }}
                    fullWidth
                    variant="contained"


                    endIcon={<Inventory2OutlinedIcon />}
                  >
                    {t("common.stockInformation") ?? "Info"}
                  </Button>

                </CardActions>

              </Card>
            </Grid>
          ))}
          {items.length === 0 && (
            <Grid item xs={12}>
              <Typography align="center">{t("common.noItemsFound")}</Typography>
            </Grid>
          )}
        </Grid>
      </LoadingSection>

      <ResponsivePagination
        page={query.page}
        pageSize={query.pageSize}
        totalCount={totalItems}
        onPageChange={handlePageChange}
      />

    </CenteredListCard>
  );
};

export default ItemList;